import { EventType } from "@/analytics/analytics-events";
import { EventBarrier } from "@/components/common/event-barrier";
import { TOGGLE_HEIGHT } from "@/components/ui/type-toggle-button";
import { selectLastCaptureDate } from "@/store/lightweight-elements-selectors";
import { useAppSelector } from "@/store/store-hooks";
import {
  CalendarIcon,
  CloseIcon,
  FaroIconButton,
  FaroMenu,
  FaroText,
  FaroTooltip,
  cyan,
  grey,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { ButtonBase } from "@mui/material";
import { Box, Stack } from "@mui/system";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState, type JSX } from "react";
import { CaptureCalendar } from "./calendar/capture-calendar";
import { CaptureLayout } from "./calendar/capture-calendar-types";
import { DAY_AND_MONTH, formatDate } from "./capture-date-format";
import {
  countCapturesInRange,
  useCaptureDateStats,
} from "./capture-date-stats";
import { LayoutToggle } from "./layout-toggle";
import { RangeEndpointButton } from "./range-endpoint-button";
import { CellUnit, RangeEndpoint, editDate, pickDate } from "./range-selection";
import { useCaptureDateFilter } from "./use-capture-date-filter";

/** Horizontal padding of the pill, tightened on the side that grows a clear button */
const PILL_PADDING_X = 1.5;

const PANEL_WIDTH = "340px";

/** Horizontal padding of the popup heading and of the calendar below it */
const HEADING_PADDING_X = 2;
const CALENDAR_PADDING_X = 1.25;

/**
 * @returns A control for the 2D overview that filters the map by capture date. It reads and
 * writes the same project-wide range as the project tree's "Time of capture" filter.
 *
 * Renders nothing for projects captured on a single day, where there is nothing to filter.
 */
export function CaptureDateFilter(): JSX.Element | null {
  const stats = useCaptureDateStats();
  const lastCaptureDate = useAppSelector(selectLastCaptureDate);
  const { startDate, endDate, isActive, setRange, clear } =
    useCaptureDateFilter();

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [layout, setLayout] = useState(CaptureLayout.list);
  const [nextEndpoint, setNextEndpoint] = useState<RangeEndpoint>();
  const [editing, setEditing] = useState<RangeEndpoint>();

  const onPick = useCallback(
    (date: DateTime, unit: CellUnit) => {
      if (editing) {
        const range = editDate(date, unit, editing, startDate, endDate);
        if (!range) return;
        setRange(...range);
        setEditing(undefined);
        setNextEndpoint(undefined);
        return;
      }
      const { range, next } = pickDate(
        date,
        unit,
        nextEndpoint ?? RangeEndpoint.start,
        startDate,
      );
      setRange(...range);
      setNextEndpoint(next);
    },
    [editing, nextEndpoint, startDate, endDate, setRange],
  );

  const clearFilter = useCallback(() => {
    Analytics.track(EventType.resetDateFilter);
    clear();
    setEditing(undefined);
    setNextEndpoint(undefined);
  }, [clear]);

  const visibleCount = useMemo(
    () => countCapturesInRange(stats, startDate, endDate),
    [stats, startDate, endDate],
  );

  // Nothing to browse or filter when every capture landed on the same day,
  // unless an existing project-wide filter still needs to be shown and cleared.
  if (stats.days.length < 2 && !isActive) return null;

  return (
    <EventBarrier>
      <Stack
        ref={setAnchorEl}
        direction="row"
        aria-label="capture date filter"
        sx={{
          alignItems: "center",
          gap: 0.75,
          pl: PILL_PADDING_X,
          pr: isActive ? 0.5 : PILL_PADDING_X,
          // Match the walk-mode scene filter toggle, and stay put when the filter changes
          height: TOGGLE_HEIGHT,
          borderRadius: "4px",
          backgroundColor: grey[1000],
          // Lifts the pill above the R3F canvas, which CanvasOverlay positions at z-index 0
          position: "relative",
          zIndex: 1,
          outline: isActive ? `1px solid ${cyan[400]}` : "none",
        }}
      >
        <ButtonBase
          aria-label="open capture date filter"
          onClick={() => {
            setEditing(undefined);
            setNextEndpoint(undefined);
            setIsOpen(true);
          }}
          sx={{ gap: 0.75, height: "100%", borderRadius: "4px" }}
        >
          <CalendarIcon
            sx={{ fontSize: "18px", color: isActive ? cyan[400] : grey[100] }}
          />
          <FaroText
            variant="bodyM"
            color={isActive ? cyan[400] : grey[100]}
            sx={{ whiteSpace: "nowrap" }}
          >
            {formatRange(startDate, endDate)}
          </FaroText>
          <FaroText
            variant="bodyS"
            color={grey[400]}
            sx={{ whiteSpace: "nowrap" }}
          >
            {isActive
              ? `${visibleCount} of ${stats.total}`
              : `${stats.total} captures`}
          </FaroText>
        </ButtonBase>

        {isActive && (
          <FaroTooltip placement="top" title="Show all capture dates">
            <FaroIconButton
              aria-label="clear capture date filter"
              onClick={clearFilter}
              color={grey[100]}
              size="s"
            >
              <CloseIcon />
            </FaroIconButton>
          </FaroTooltip>
        )}
      </Stack>

      {anchorEl && (
        <FaroMenu
          dark
          anchorEl={anchorEl}
          open={isOpen}
          onClose={() => setIsOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          // Fixed, so nothing shifts under the pointer as labels change length
          minWidth={PANEL_WIDTH}
          sx={{ mt: 0.5, "& .MuiMenu-paper": { width: PANEL_WIDTH } }}
        >
          <Stack sx={{ px: HEADING_PADDING_X, pt: 1.5, pb: 0.5, gap: 0.5 }}>
            <Stack
              direction="row"
              sx={{ justifyContent: "space-between", alignItems: "center" }}
            >
              <FaroText variant="labelS" color={grey[300]}>
                Select date range
              </FaroText>
              {/* Lined up with the right edge of the list and calendar below */}
              <Box
                component="span"
                sx={{ mr: CALENDAR_PADDING_X - HEADING_PADDING_X }}
              >
                <LayoutToggle value={layout} onChange={setLayout} />
              </Box>
            </Stack>
            <Stack
              direction="row"
              sx={{ alignItems: "center", justifyContent: "space-between" }}
            >
              <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
                <RangeEndpointButton
                  label="Start"
                  date={startDate}
                  isEditing={editing === RangeEndpoint.start}
                  onClick={() => setEditing(RangeEndpoint.start)}
                />
                <FaroText variant="heading20" color={grey[500]}>
                  –
                </FaroText>
                <RangeEndpointButton
                  label="End"
                  date={endDate}
                  isEditing={editing === RangeEndpoint.end}
                  onClick={() => setEditing(RangeEndpoint.end)}
                />
              </Stack>
              {/* Hidden rather than removed, so the row keeps its height */}
              <FaroTooltip
                placement="top"
                title={isActive ? "Clear selection" : ""}
              >
                <FaroIconButton
                  aria-label="clear selection"
                  onClick={clearFilter}
                  disabled={!isActive}
                  color={grey[100]}
                  size="s"
                  sx={{
                    mr: CALENDAR_PADDING_X - HEADING_PADDING_X,
                    visibility: isActive ? "visible" : "hidden",
                  }}
                >
                  <CloseIcon />
                </FaroIconButton>
              </FaroTooltip>
            </Stack>
          </Stack>

          <Box component="div" sx={{ px: CALENDAR_PADDING_X, py: 1 }}>
            <CaptureCalendar
              layout={layout}
              stats={stats}
              initialMonth={DateTime.fromJSDate(
                startDate ?? lastCaptureDate ?? new Date(),
              )}
              startDate={startDate}
              endDate={endDate}
              onPick={onPick}
              isEditing={!!editing || nextEndpoint === RangeEndpoint.end}
            />
          </Box>
        </FaroMenu>
      )}
    </EventBarrier>
  );
}

/**
 * @returns A compact label for the active range, collapsing a single-day range to one date
 * @param start Inclusive start of the range
 * @param end Inclusive end of the range
 */
function formatRange(start?: Date, end?: Date): string {
  // The project tree filter can leave one side open
  if (!start || !end) {
    if (start) return `From ${formatDate(start)}`;
    if (end) return `Until ${formatDate(end)}`;
    return "All capture dates";
  }
  const from = DateTime.fromJSDate(start);
  const to = DateTime.fromJSDate(end);
  if (from.hasSame(to, "day")) return formatDate(from);
  const fromFormat = from.hasSame(to, "year") ? DAY_AND_MONTH : undefined;
  return `${formatDate(from, fromFormat)} – ${formatDate(to)}`;
}
